import React, { useState, useEffect, useRef } from "react";
import useSession from "Utils/Hooks/useSession";
import { PlaidLink } from "react-plaid-link";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import { useHistory } from "react-router-dom";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useRedirectUrl from "Utils/Hooks/useRedirectUrl";
import VIPOnlineLogo from "Assets/Img/vipo-logo.png";
import bankIcon from "Assets/Icons/bank.svg";
import Loading from "Components/Loading";
import { Button, Col, Form, Row } from "react-bootstrap";
import useNative from "Utils/Hooks/useNative";
import useOAuthStateId from "Utils/Hooks/useOAuthStateId";
import useAppKey from "Utils/Hooks/useAppKey";
import AddFundingSourceError from "Assets/Img/AddFundingSourceError.png";
import "./OnlineEnrollment.scss";
import { Connect } from "connect-web-sdk";
import Finicity from "Components/FundingSources/Finicity";
import MaskedField from "Components/Fields/MaskedField";
import CancelButton from "Components/CancelButton";
import {
  EnrollmentSteps,
  fromOperatorDefault,
  Validators,
} from "Utils/Constants";
import { faro } from "@grafana/faro-react";
import { useLastEnrollmentStep } from "context/LastEnrollmentStepProvider";
import AddAccountSuccess from "Components/AddAccountSuccess";
import TermsAndConditions from "Components/TermsAndConditions";

const OnlineEnrollment = ({ plaidRedirect }) => {
  const vipApi = useVipApiFetcher();
  const mode = useMode();
  const { session, fetchSession, updateSession } = useSession();
  const [linkToken, setLinkToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [divStyle, setDivStyle] = useState({ display: "none" });
  const history = useHistory();
  const isNative = useNative();
  const oauth_state_id = useOAuthStateId();
  const navigateToDeposit = mode === MODE.deposit;
  const navigateToWithdraw = mode === MODE.withdraw;
  const [receivedRedirectUri, setReceivedRedirectUri] = useState(null);
  const url = useRedirectUrl();
  const appKey = useAppKey();
  const refTimeOut = useRef(false);
  const [mitekRedirect, setMitekRedirect] = useState(
    session?.mitekValidationStage !== "None" ? true : false
  );
  const mitekLink =
    session?.mitekValidationStage !== "None" ? session?.mitekLink : null;
  const [connectUrl, setConnectUrl] = useState(null);
  const [fromOperator, setFromOperator] = useState(fromOperatorDefault);
  const identificationInfoEnabled = session?.toggles.identificationInfoEnabled;
  const [ssn, setSSN] = useState(null);
  const [showFundingPage, setShowFundingPage] = useState(null);
  const [showConfirmEnrollment, setShowConfirmEnrollment] = useState(false);
  const { setLastEnrollmentStep, LastEnrollmentStep } = useLastEnrollmentStep();
  const [acceptedEnrollmentFee, setAcceptedEnrollmentFee] = useState(false);
  const [enrollmentFeeEnabled, setEnrollmentFeeEnabled] = useState(false);
  const [enrollmentFeeAmount, setEnrollmentFeeAmount] = useState("");

  const style = {
    width: "100%",
    height: "66vh",
    overflow: "hidden",
    border: "none",
    align: "center",
  };

  useEffect(() => {
    if (!vipApi.ready) return;
    vipApi
      .fetch("/api/enrollment")
      .then((res) => res.json())
      .then((data) => {
        if (session.toggles.identificationInfoEnabled && plaidRedirect) {
          let bankFields = {
            bank: data.bank,
          };
          setFromOperator(bankFields);
        } else {
          setFromOperator(data);
        }
        faro.api.pushEvent("Starting Online Enrollment");
      });
  }, [vipApi.ready]);

  useEffect(() => {
    const poll = async function (ms) {
      let result = await vipApi.fetch("/api/mitek/status");
      while (result.status === 202 && !refTimeOut.current) {
        updateSession(await result.json());
        await wait(ms);
        result = await vipApi.fetch("/api/mitek/status");
      }
      return result;
    };

    const wait = function (ms = 5000) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    };
    async function fetchData() {
      if (mitekRedirect) {
        setIsLoading(false);
        const responseCode = await poll(10000);
        if (responseCode.status === 200 && !refTimeOut.current) {
          setMitekRedirect(false);
          await fetchSession(true);
        } else {
          onEnrollError("Mitek Redirect Error");
        }
      }
    }
    fetchData();
  }, [mitekRedirect]);

  useEffect(() => {
    setEnrollmentFeeEnabled(session?.merchantEnrollmentFeeEnabled);
    setEnrollmentFeeAmount(session?.merchantEnrollmentFeeAmount?.toString());
  }, [session]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "timeout") {
        refTimeOut.current = true;
      }
    };
    window.addEventListener("message", handleMessage);

    if (plaidRedirect) {
      setLinkToken(localStorage.getItem("link_token"));
      setReceivedRedirectUri(
        url
          ? url + "?oauth_state_id=" + oauth_state_id
          : window.location.href.includes("oauth_state_id=")
            ? window.location.href
            : window.location.href + "?oauth_state_id=" + oauth_state_id
      );
      // setReceivedRedirectUri("https://cdn.plaid.com/link/v2/stable/oauth.html?oauth_state_id=" + oauth_state_id);
      setIsLoading(false);
    }

    let connectUrlReceived = false;
    (async () => {
      if (session.toggles.finicityEnabled) {
        const result = await vipApi.fetchData("/api/finicity/getUrl");
        if (result) {
          setConnectUrl(result);
          connectUrlReceived = result ? true : false;
        } else {
          faro.api.pushError(new Error("Fetch Finicity Connect URL Error"), {
            context: result,
          });
        }
        setIsLoading(false);
      }

      if (
        session.toggles.plaidEnabled &&
        !connectUrlReceived &&
        !plaidRedirect
      ) {
        let mounted = true;
        let oprUrl = "";
        if (url) {
          oprUrl = appKey
            ? "?redirectUri=" + url + "/" + appKey
            : "?redirectUri=" + url;
        } else {
          oprUrl = appKey
            ? "?redirectUri=" + window.location.href + "/" + appKey
            : "?redirectUri=" + window.location.href + "/" + session.merchantId;
        }
        try {
          await vipApi.fetch("/api/plaid/link" + oprUrl).then((res) => {
            res.json().then((data) => {
              if (!mounted) return;
              if (res.ok) {
                setLinkToken(data.linkToken);
                localStorage.setItem("link_token", data.linkToken);
              }
              if (!res.ok) {
                faro.api.pushError(new Error("Plaid Link Token Error"), {
                  context: res,
                });
              }
              setIsLoading(false);
            });
          });
        } catch (error) {
          console.error(error);
          onEnrollError("Plaid Link Token Error");
        }
        return () => {
          mounted = false;
        };
      }

      if (!session.toggles.plaidEnabled && !session.toggles.finicityEnabled) {
        setIsLoading(false);
      }
    })();

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const openNativeLink = () => {
    window.$onAndroidSuccess = function (metadata) {
      onAndroidSuccess(metadata);
    };

    window.$onAndroidError = function (error) {
      onAndroidError(error);
    };
    let message = {
      linkToken: linkToken,
    };

    var plaidTokenForAndroid = linkToken;
    if (/android/i.test(navigator.userAgent) || window?.Android) {
      handleAndroidMessage(plaidTokenForAndroid);
    }
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) || window?.webkit) {
      var iosPromise =
        window?.webkit?.messageHandlers?.NativeBridge?.postMessage(message);
      iosPromise
        .then(
          function (result) {
            onSuccessDefault(null, result);
          },
          function (err) {
            console.log(err);
          }
        )
        .finally(() => {
          //iosPromise.dispose();
        });
    }
  };

  function handleAndroidMessage(message) {
    return new Promise((resolve, reject) => {
      window?.Android?.openLinkNative(
        message,
        onAndroidSuccess.name,
        onAndroidError.name
      );
    });
  }

  function onAndroidSuccess(metadata) {
    console.log("onAndroidSuccess called");
    console.log(metadata);
    onSuccessDefault(null, metadata);
  }

  function onAndroidError(error) {
    console.log("onAndroidError called");
    console.log(error);
  }

  const onFieldChange = (fieldName, fieldValue, isValid) => {
    if (isValid) {
      setSSN(fieldValue);
    } else {
      setSSN(undefined);
    }
  };

  const onSaveSsn = async (event) => {
    event.preventDefault();
    try {
      await vipApi
        .fetch("/api/accounts/ssn", "PUT", {
          ssn: ssn,
        })
        .then((res) => {
          if (res.ok) {
            faro.api.pushEvent("SSN saved successfully");
          }
        });
    } catch (error) {
      faro.api.pushError(
        new Error("Online Enrollment SSN Update Network Error"),
        { context: error }
      );
    }
    setShowFundingPage(true);
  };

  const onSuccessDefault = async (_, metadata) => {
    setIsLoading(true);
    faro.api.pushEvent("Enrolling with Plaid Account");
    try {
      await vipApi
        .fetch("/api/plaid/account", "PUT", {
          accountId: metadata.account_id,
          accountToken: metadata.public_token,
        })
        .then((res) => {
          res.json().then((data) => {
            setIsLoading(false);
            setLastEnrollmentStep(true);
            setShowConfirmEnrollment(true);
            if (!res.ok) {
              faro.api.pushError(new Error("Plaid Update Error"), {
                context: data,
              });
            }
          });
        });
    } catch (error) {
      faro.api.pushError(
        new Error("Online Enrollment Plaid Account Update Network Error"),
        { context: error }
      );
    }
  };

  const onSubmitCommon = async (url = "/api/plaid/enroll") => {
    setIsLoading(true);
    const response = await vipApi.fetch(url, "POST");
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const body = await response.json();
      if (body.status) {
        if (body.status === "success") {
          onEnrollSuccess();
        } else if (body.status === "noBanks") {
          await fetchSession(true);
          history.push({ pathname: "/addAccount", state: { nobank: true } });
        } else if (body.status === "failure") {
          onFailure(body);
        } else {
          onEnrollError(body);
        }
      } else {
        onEnrollError(body);
      }
    } else {
      onEnrollError("Plaid Response is not JSON");
    }
    localStorage.removeItem("link_token");
  };

  const onFinicityEnrollSubmit = async () => {
    setIsLoading(true);
    const response = await vipApi.fetch("/api/finicity/enroll", "POST");
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const body = await response.json();
      if (body.status) {
        if (body.status === "success") {
          onEnrollSuccess();
        } else if (body.status === "noBanks") {
          await fetchSession(true);
          history.push({ pathname: "/addAccount", state: { nobank: true } });
        } else if (body.status === "failure") {
          onFailure(body);
        } else {
          onEnrollError(body);
        }
      } else {
        onEnrollError(body);
      }
    } else {
      onEnrollError("Fincity Response is not JSON");
    }
  };

  function onFailure(body) {
    faro.api.pushError(
      new Error(
        `Online Enrollment Failure - ${body.errorModel.patronErrorMessage ? body.errorModel.patronErrorMessage : body}`
      )
    );
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        fromOperator,
        showHeader: false,
        allowRetry: false,
        onClickClose: true,
        title: "Account Not Created",
        message: `${body.errorModel.patronErrorMessage ? body.errorModel.patronErrorMessage : body}.`,
        customButtons: [
          {
            label: `CONTINUE TO ${session.operatorName?.toUpperCase()}`,
            returnToOperator: true,
            returnUrl: session?.returnUrl,
            transactionId: session?.transactionId,
            transactionType: navigateToWithdraw ? "withdraw" : "deposit",
          },
        ],
      },
    });
  }

  async function onEnrollSuccess() {
    // Fetch updated session data - should now show as enrollment complete.
    await fetchSession(true);
    faro.api.pushEvent("Account Created", { session: session }, "session");
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: true,
        isError: false,
        title: "Account Created",
        message:
          "Your VIP account has been created and you are now able to make transactions.",
        navigateTo:
          mode === MODE.manageBanks
            ? "myAccounts"
            : navigateToDeposit
              ? "addFunds"
              : navigateToWithdraw
                ? "withdrawFunds"
                : "dashboard",
      },
    });
  }

  function onEnrollError(body) {
    faro.api.pushError(new Error("Account Not Created"), { context: body });
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        onClickClose: true,
        title: "Account Not Created",
        message:
          "We are unable to complete this request. Please contact our Customer Service Team at 1-800-500-1973. Reference code E-28 so that we may better serve you.",
        buttonText: `CONTINUE TO ${session.operatorName.toUpperCase()}`,
      },
    });
  }

  const onExit = (error, metadata) => {
    window.sessionStorage.setItem("plaidlaunch", false);
    setDivStyle({ display: "block" });
    console.log(error, metadata);
    faro.api.pushEvent("User exited Plaid");
  };

  const showDiv = {
    display: "block",
  };

  const loadHandler = () => {
    if (!LastEnrollmentStep) {
      if (session.toggles.finicityEnabled && connectUrl) {
        faro.api.pushEvent("Launching Finicity");
        launchFinicity();
      } else {
        window.sessionStorage.setItem("plaidlaunch", true);
        if (true) {
          const open = document.getElementsByClassName("ready")[0];
          open.click();
          if (!plaidRedirect) {
            setIsLoading(false);
          }
          linkToken ?? faro.api.pushEvent("Launching Plaid");
        }
      }
    }
  };

  const launchFinicity = () => {
    const connectEventHandlers = {
      onDone: (event) => {
        console.log("onDone", event);
        faro.api.pushEvent("Enrolling with Finicity Account");
        setLastEnrollmentStep(true);
        setShowConfirmEnrollment(true);
      },
      onCancel: (event) => {
        setDivStyle({ display: "block" });
        console.log("onCancel", event);
        faro.api.pushEvent("User exited Finicity");
      },
      onError: (event) => {
        console.log("onError", event);
        onEnrollError("Finicity Launch Error");
      },
      onUser: ({ action, code, customerId }) => {
        if (action === "End" && code === 200 && customerId) {
          faro.api.pushEvent(
            "Add Finicity Account Success",
            { customerId: customerId },
            "session"
          );
        }
      },
      onLoad: () => {
        console.log("loaded");
      },
    };
    const connectOptions = {
      overlay: "rgba(199,201,199, 0.5);z-index: 10000;",
    };
    Connect.launch(connectUrl, connectEventHandlers, connectOptions);
  };

  let button;

  if (isLoading || !session) {
    button = <Loading />;
  } else {
    button = !isNative ? (
      <div style={divStyle} onLoad={loadHandler}>
        {session.toggles.plaidEnabled && !connectUrl ? (
          <PlaidLink
            token={linkToken}
            onSuccess={onSuccessDefault}
            onExit={onExit}
            className="ready"
            onLoad={loadHandler}
          >
            <img
              className="bank-icon  click-add-plaid"
              src={bankIcon}
              alt="Bank Icon"
            ></img>
            <div className="bank-label click-add-plaid">
              CONNECT YOUR BANK ACCOUNT
            </div>
          </PlaidLink>
        ) : (
          <Finicity connectUrl={connectUrl} />
        )}
      </div>
    ) : (
      <Button className="ready" onClick={openNativeLink}>
        <img
          className="bank-icon click-add-plaid"
          src={bankIcon}
          alt="Bank Icon"
        ></img>
        <div className="bank-label click-add-plaid">
          CONNECT YOUR BANK ACCOUNT
        </div>
      </Button>
    );
  }

  let plaidFundingSourceError = (
    <>
      <div
        style={{ width: "100%", display: "inline-block", textAlign: "center" }}
      >
        <div
          className="add-funding-source-error"
          style={{ width: "60%", display: "inline-block", textAlign: "center" }}
        >
          <img
            src={AddFundingSourceError}
            alt="Add Funding Source Error"
            style={{ paddingTop: "5px", paddingBottom: "10px" }}
          ></img>
          <p>We are unable to add new accounts at this time.</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      {plaidRedirect && (
        <PlaidLink
          token={linkToken}
          onSuccess={onSuccessDefault}
          className="ready"
          onLoad={loadHandler}
          receivedRedirectUri={receivedRedirectUri}
          onExit={onExit}
        ></PlaidLink>
      )}
      {isLoading ? (
        <Loading></Loading>
      ) : (
        <>
          <div style={showDiv}>
            <div className="enrollment-header pb-2 mt-3">
              <img
                className="vip-preferred"
                src={VIPOnlineLogo}
                alt="VIP Online"
              ></img>
              <br />
              <span className="title">Account Sign Up</span>
            </div>
          </div>
          {mitekRedirect ? (
            <div style={style}>
              <iframe
                src={mitekLink}
                allow="autoplay; camera; microphone"
                title="Verification Process"
                style={style}
              />
            </div>
          ) : (
            <>
              <div className="pt-4">
                {!showFundingPage &&
                identificationInfoEnabled &&
                fromOperator.id?.includes(EnrollmentSteps.ID.SSN) ? (
                  <>
                    <Row>
                      <Col xs={12} md={{ span: 6, offset: 3 }}>
                        <div className="sub-title">Identification Info</div>
                        <Form id="ssnForm" onSubmit={onSaveSsn}>
                          <MaskedField
                            fieldName="ssn"
                            label="SSN#"
                            mask="999-99-9999"
                            maxLength={9}
                            required={true}
                            autoComplete="off"
                            validators={[Validators.SSN]}
                            onValueChanged={onFieldChange}
                            disabled={false}
                          />
                          <button
                            type="submit"
                            className={
                              ssn
                                ? "submit-button click-enroll-next"
                                : "submit-button disabled"
                            }
                            disabled={!ssn}
                          >
                            NEXT
                          </button>
                        </Form>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <div className="funding-source">
                    {connectUrl || linkToken ? button : plaidFundingSourceError}
                  </div>
                )}
              </div>
              {showConfirmEnrollment && LastEnrollmentStep && (
                <div>
                  <AddAccountSuccess />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "30px",
                    }}
                  >
                    <button
                      className={`submit-button ${enrollmentFeeEnabled && !acceptedEnrollmentFee && "disabled"}`}
                      onClick={() => {
                        if (session.toggles.finicityEnabled && connectUrl) {
                          onFinicityEnrollSubmit();
                        } else {
                          onSubmitCommon();
                        }
                      }}
                      disabled={enrollmentFeeEnabled && !acceptedEnrollmentFee}
                    >
                      SUBMIT
                    </button>
                  </div>
                </div>
              )}

              {enrollmentFeeEnabled && LastEnrollmentStep && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontFamily: "Titillium Web",
                    marginTop: "20px",
                  }}
                >
                  <div className="enrollment-fee-checkbox">
                    <input
                      type="checkbox"
                      onChange={() => {
                        setAcceptedEnrollmentFee((cur) => !cur);
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <p style={{ margin: 0 }}>
                      By checking this box, you agree to pay a $
                      {enrollmentFeeAmount.split(".")[0]} enrollment fee.
                      Pavilion reserves the right to waive this fee at its sole
                      discretion.
                    </p>
                  </div>
                </div>
              )}
              {showConfirmEnrollment && LastEnrollmentStep && (
                <div className="text-center" style={{ marginTop: "20px" }}>
                  <TermsAndConditions buttonText="submit" />
                </div>
              )}
              {(connectUrl || linkToken) && (
                <CancelButton
                  btnText="Account Sign Up"
                  stage="Enrollment"
                ></CancelButton>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
export default OnlineEnrollment;
